/**
 * Eye Test Booking — Multi-step booking form powered by Easy!Appointments.
 * Web component: <eye-test-booking>
 */
class EyeTestBooking extends HTMLElement {

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    try {
      this.config = JSON.parse(this.querySelector('[data-etb-config]').textContent);
    } catch (e) {
      console.error('Eye test booking: invalid config', e);
      return;
    }
    this.s = this.config.strings;

    this.state = {
      step: 1,
      country: null,
      providers: [],
      services: [],
      countryGroups: { BH: [], QA: [] },
      selectedProvider: null,
      selectedService: null,
      selectedDate: null,
      selectedTime: null,
      availableSlots: [],
      customer: { firstName: '', lastName: '', email: '', phone: '', notes: '' },
      isLoading: false,
      slotsLoading: false,
      booking: null,
    };

    this.loader = this.querySelector('[data-etb-loader]');
    this.errorEl = this.querySelector('[data-etb-error]');
    this.contentEl = this.querySelector('[data-etb-content]');

    if (typeof Shopify !== 'undefined' && Shopify.designMode) {
      this.renderDesignMode();
      return;
    }

    this.loadInitialData();
  }

  // ── API Layer ──

  async apiFetch(action, params = {}, method = 'GET', body = null) {
    const url = new URL(this.config.proxyUrl);
    url.searchParams.set('action', action);
    Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 30000);

    const opts = { method, headers: {}, signal: controller.signal };
    if (body) {
      opts.headers['Content-Type'] = 'application/json';
      opts.body = JSON.stringify(body);
    }

    try {
      const res = await fetch(url.toString(), opts);
      clearTimeout(timeout);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || this.s.errorGeneric);
      }
      return res.json();
    } catch (e) {
      clearTimeout(timeout);
      if (e.name === 'AbortError') {
        throw new Error('Request timed out. Please try again.');
      }
      throw e;
    }
  }

  async loadInitialData() {
    try {
      const [services, providers] = await Promise.all([
        this.apiFetch('services'),
        this.apiFetch('providers'),
      ]);

      this.state.services = Array.isArray(services) ? services : [];
      this.state.providers = Array.isArray(providers) ? providers : [];
      this.state.countryGroups = this.groupProvidersByCountry(this.state.providers);

      this.loader.style.display = 'none';
      this.render();
    } catch (e) {
      this.loader.style.display = 'none';
      this.showError(e.message || this.s.errorGeneric);
    }
  }

  // ── Country Grouping ──

  groupProvidersByCountry(providers) {
    const groups = { BH: [], QA: [] };
    const qatarKeywords = ['qatar', 'doha', 'lusail', 'al wakra', 'vendome', 'festival city'];

    providers.forEach(p => {
      const text = `${p.city || ''} ${p.address || ''} ${p.firstName || ''} ${p.lastName || ''}`.toLowerCase();
      const isQatar = qatarKeywords.some(kw => text.includes(kw));
      groups[isQatar ? 'QA' : 'BH'].push(p);
    });

    return groups;
  }

  getServicesForCountry(country) {
    return this.state.services.filter(s => {
      const name = (s.name || '').toUpperCase();
      if (name.includes(' BH')) return country === 'BH';
      if (name.includes(' QA')) return country === 'QA';
      return true;
    });
  }

  /**
   * Filter services to only those the selected provider actually offers.
   * EA providers have a `services` array of service IDs they support.
   */
  getServicesForProvider() {
    const provider = this.state.selectedProvider;
    if (!provider) return [];
    const providerServiceIds = provider.services || [];
    // Intersect: country-filtered services that this provider supports
    const countryServices = this.getServicesForCountry(this.state.country);
    return countryServices.filter(s => providerServiceIds.includes(s.id));
  }

  /**
   * Check if the selected provider is open on a given day of week.
   * Returns the working plan entry or null if closed.
   * dayIndex: 0=sunday, 1=monday, ... 5=friday, 6=saturday
   */
  getProviderWorkingDay(dayIndex) {
    const provider = this.state.selectedProvider;
    if (!provider?.settings?.workingPlan) return null;
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    return provider.settings.workingPlan[dayNames[dayIndex]] || null;
  }

  /**
   * Check if a date string (YYYY-MM-DD) falls on a day the provider is closed.
   */
  isProviderClosedOnDate(dateStr) {
    const date = new Date(dateStr + 'T00:00:00');
    const dayIndex = date.getDay(); // 0=Sun, 5=Fri, 6=Sat
    return !this.getProviderWorkingDay(dayIndex);
  }

  getProviderName(p) {
    return [p.firstName, p.lastName].filter(Boolean).join(' ') || 'Branch';
  }

  /**
   * Return list of day names when a provider is closed (workingPlan entry is null).
   */
  getClosedDays(provider) {
    const wp = provider?.settings?.workingPlan;
    if (!wp) return [];
    const dayLabels = {
      sunday: this.s.daySun || 'Sun',
      monday: this.s.dayMon || 'Mon',
      tuesday: this.s.dayTue || 'Tue',
      wednesday: this.s.dayWed || 'Wed',
      thursday: this.s.dayThu || 'Thu',
      friday: this.s.dayFri || 'Fri',
      saturday: this.s.daySat || 'Sat',
    };
    return Object.entries(wp)
      .filter(([_, plan]) => !plan)
      .map(([day]) => dayLabels[day] || day);
  }

  // ── Rendering ──

  render() {
    this.hideError();
    const step = this.state.step;
    let html = '';

    if (step >= 1 && step <= 5) {
      html += this.renderProgress(step);
    }

    switch (step) {
      case 1: html += this.renderCountryStep(); break;
      case 2: html += this.renderBranchStep(); break;
      case 3: html += this.renderServiceStep(); break;
      case 4: html += this.renderDateTimeStep(); break;
      case 5: html += this.renderDetailsStep(); break;
      case 6: html += this.renderSuccessStep(); break;
    }

    this.contentEl.innerHTML = html;
    this.bindStepEvents();
  }

  renderProgress(active) {
    const steps = [
      this.s.selectCountry,
      this.s.selectBranch,
      this.s.selectService,
      this.s.selectDateTime,
      this.s.yourDetails,
    ];
    let html = '<div class="etb-progress">';
    steps.forEach((label, i) => {
      const num = i + 1;
      const cls = num === active ? 'etb-progress__step--active'
                : num < active ? 'etb-progress__step--complete' : '';
      if (i > 0) html += '<span class="etb-progress__sep"></span>';
      html += `<div class="etb-progress__step ${cls}">
        <span class="etb-progress__num">${num}</span>
        <span class="etb-progress__label">${this.esc(label)}</span>
      </div>`;
    });
    html += '</div>';
    return html;
  }

  renderCountryStep() {
    const bh = this.state.countryGroups.BH;
    const qa = this.state.countryGroups.QA;

    return `<div class="etb-step">
      <h3 class="etb-step__title">${this.esc(this.s.selectCountry)}</h3>
      <div class="etb-countries">
        <button type="button" class="etb-country-card" data-action="select-country" data-country="BH">
          <span class="etb-country-card__flag">\u{1F1E7}\u{1F1ED}</span>
          <span class="etb-country-card__name">${this.esc(this.s.bahrain)}</span>
          <span class="etb-country-card__count">${bh.length} ${this.esc(this.s.branches)}</span>
        </button>
        <button type="button" class="etb-country-card" data-action="select-country" data-country="QA">
          <span class="etb-country-card__flag">\u{1F1F6}\u{1F1E6}</span>
          <span class="etb-country-card__name">${this.esc(this.s.qatar)}</span>
          <span class="etb-country-card__count">${qa.length} ${this.esc(this.s.branches)}</span>
        </button>
      </div>
    </div>`;
  }

  renderBranchStep() {
    const branches = this.state.countryGroups[this.state.country] || [];
    let cards = '';

    branches.forEach(p => {
      const name = this.getProviderName(p);
      const addr = p.address || '';
      const phone = p.phone || p.mobile || '';
      const serviceCount = (p.services || []).length;
      const closedDays = this.getClosedDays(p);

      cards += `<button type="button" class="etb-branch-card${serviceCount === 0 ? ' etb-branch-card--no-services' : ''}" data-action="select-branch" data-id="${p.id}" ${serviceCount === 0 ? 'disabled' : ''}>
        <p class="etb-branch-card__name">${this.esc(name)}</p>
        ${addr ? `<p class="etb-branch-card__detail">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
          ${this.esc(addr)}
        </p>` : ''}
        ${phone ? `<p class="etb-branch-card__detail">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/></svg>
          ${this.esc(phone)}
        </p>` : ''}
        ${closedDays.length > 0 ? `<p class="etb-branch-card__closed">
          ${this.esc((this.s.closedOn || 'Closed on') + ': ' + closedDays.join(', '))}
        </p>` : ''}
        ${serviceCount === 0 ? `<p class="etb-branch-card__closed">${this.esc(this.s.noServicesAvailable || 'No services available')}</p>` : ''}
      </button>`;
    });

    return `<div class="etb-step">
      <h3 class="etb-step__title">${this.esc(this.s.selectBranch)}</h3>
      <div class="etb-branches">${cards}</div>
      <div class="etb-nav">
        <button type="button" class="etb-btn etb-btn--secondary" data-action="back">${this.esc(this.s.back)}</button>
        <span></span>
      </div>
    </div>`;
  }

  renderServiceStep() {
    const services = this.getServicesForProvider();
    let cards = '';

    if (services.length === 0) {
      cards = `<p class="etb-no-slots">${this.esc(this.s.noServicesAvailable || 'No services available at this branch.')}</p>`;
    } else {
      services.forEach(s => {
        const duration = s.duration ? `${s.duration} min` : '';
        const icon = (s.name || '').toLowerCase().includes('colour') ? '\u{1F7E2}' : '\u{1F441}';

        cards += `<button type="button" class="etb-service-card" data-action="select-service" data-id="${s.id}">
          <span class="etb-service-card__icon">${icon}</span>
          <span class="etb-service-card__name">${this.esc(s.name || '')}</span>
          ${duration ? `<span class="etb-service-card__duration">${this.esc(duration)}</span>` : ''}
        </button>`;
      });
    }

    return `<div class="etb-step">
      <h3 class="etb-step__title">${this.esc(this.s.selectService)}</h3>
      <div class="etb-services">${cards}</div>
      <div class="etb-nav">
        <button type="button" class="etb-btn etb-btn--secondary" data-action="back">${this.esc(this.s.back)}</button>
        <span></span>
      </div>
    </div>`;
  }

  renderDateTimeStep() {
    const p = this.state.selectedProvider;
    const name = this.getProviderName(p);
    const today = new Date().toISOString().split('T')[0];

    let slotsHtml = '';
    if (this.state.slotsLoading) {
      slotsHtml = `<div class="etb-loading" style="padding:2rem 0;"><span class="etb-inline-spinner"></span> ${this.esc(this.s.loadingSlots)}</div>`;
    } else if (this.state.selectedDate) {
      if (this.state.closedDay) {
        slotsHtml = `<p class="etb-no-slots">${this.esc(this.s.branchClosedDay || 'This branch is closed on the selected day. Please choose another date.')}</p>`;
      } else if (this.state.availableSlots.length === 0) {
        slotsHtml = `<p class="etb-no-slots">${this.esc(this.s.noSlots)}</p>`;
      } else {
        slotsHtml = '<div class="etb-slots">';
        this.state.availableSlots.forEach(time => {
          const active = time === this.state.selectedTime ? 'etb-slot--active' : '';
          slotsHtml += `<button type="button" class="etb-slot ${active}" data-action="select-time" data-time="${this.esc(time)}">${this.esc(time)}</button>`;
        });
        slotsHtml += '</div>';
      }
    }

    return `<div class="etb-step">
      <h3 class="etb-step__title">${this.esc(this.s.selectDateTime)}</h3>
      <div class="etb-datetime">
        <div class="etb-datetime__info">
          <h4 class="etb-datetime__info-title">${this.esc(name)}</h4>
          ${p.address ? `<div class="etb-datetime__info-row">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
            <span>${this.esc(p.address)}</span>
          </div>` : ''}
          ${(p.phone || p.mobile) ? `<div class="etb-datetime__info-row">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/></svg>
            <span>${this.esc(p.phone || p.mobile)}</span>
          </div>` : ''}
          <div class="etb-datetime__info-row">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
            <span>${this.esc(this.state.selectedService?.name || '')}</span>
          </div>
        </div>
        <div class="etb-datetime__picker">
          <div>
            <p class="etb-date-label">${this.esc(this.s.pickDate)}</p>
            <input type="date" class="etb-date-input" data-action="select-date" min="${today}" value="${this.state.selectedDate || ''}">
          </div>
          ${this.state.selectedDate ? `<div>
            <p class="etb-slots-label">${this.esc(this.s.availableSlots)}</p>
            ${slotsHtml}
          </div>` : ''}
        </div>
      </div>
      <div class="etb-nav">
        <button type="button" class="etb-btn etb-btn--secondary" data-action="back">${this.esc(this.s.back)}</button>
        <button type="button" class="etb-btn etb-btn--primary" data-action="next-to-details" ${this.state.selectedTime ? '' : 'disabled'}>${this.esc(this.s.next)}</button>
      </div>
    </div>`;
  }

  renderDetailsStep() {
    const p = this.state.selectedProvider;
    const svc = this.state.selectedService;
    const c = this.state.customer;

    return `<div class="etb-step">
      <h3 class="etb-step__title">${this.esc(this.s.yourDetails)}</h3>
      <div class="etb-details">
        <div class="etb-summary">
          <h4 class="etb-summary__title">${this.esc(this.s.summary)}</h4>
          <div class="etb-summary__row">
            <span class="etb-summary__label">${this.state.country === 'BH' ? this.esc(this.s.bahrain) : this.esc(this.s.qatar)}</span>
            <span class="etb-summary__value">${this.esc(this.getProviderName(p))}</span>
          </div>
          <div class="etb-summary__row">
            <span class="etb-summary__label">${this.esc(this.s.serviceLabel || 'Service')}</span>
            <span class="etb-summary__value">${this.esc(svc?.name || '')}</span>
          </div>
          <div class="etb-summary__row">
            <span class="etb-summary__label">${this.esc(this.s.dateLabel || 'Date')}</span>
            <span class="etb-summary__value">${this.formatDate(this.state.selectedDate)}</span>
          </div>
          <div class="etb-summary__row">
            <span class="etb-summary__label">${this.esc(this.s.timeLabel || 'Time')}</span>
            <span class="etb-summary__value">${this.esc(this.state.selectedTime || '')}</span>
          </div>
        </div>
        <div class="etb-form" data-etb-form>
          <div class="etb-form__row">
            <div class="etb-form__field">
              <label class="etb-form__label">${this.esc(this.s.firstName)} *</label>
              <input type="text" class="etb-form__input" data-field="firstName" value="${this.esc(c.firstName)}" required autocomplete="given-name">
            </div>
            <div class="etb-form__field">
              <label class="etb-form__label">${this.esc(this.s.lastName)}</label>
              <input type="text" class="etb-form__input" data-field="lastName" value="${this.esc(c.lastName)}" autocomplete="family-name">
            </div>
          </div>
          <div class="etb-form__field">
            <label class="etb-form__label">${this.esc(this.s.email)} *</label>
            <input type="email" class="etb-form__input" data-field="email" value="${this.esc(c.email)}" required autocomplete="email">
          </div>
          <div class="etb-form__field">
            <label class="etb-form__label">${this.esc(this.s.phone)} *</label>
            <input type="tel" class="etb-form__input" data-field="phone" value="${this.esc(c.phone)}" required autocomplete="tel">
          </div>
          <div class="etb-form__field">
            <label class="etb-form__label">${this.esc(this.s.notes)}</label>
            <textarea class="etb-form__input etb-form__textarea" data-field="notes" rows="3">${this.esc(c.notes)}</textarea>
          </div>
          <div class="etb-nav" style="margin-top:0.8rem;">
            <button type="button" class="etb-btn etb-btn--secondary" data-action="back">${this.esc(this.s.back)}</button>
            <button type="button" class="etb-btn etb-btn--primary" data-action="submit-booking">${this.esc(this.s.confirm)}</button>
          </div>
        </div>
      </div>
    </div>`;
  }

  renderSuccessStep() {
    const b = this.state.booking;
    const p = this.state.selectedProvider;
    const svc = this.state.selectedService;

    return `<div class="etb-success">
      <svg class="etb-success__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M22 11.08V12a10 10 0 11-5.93-9.14"/>
        <polyline points="22 4 12 14.01 9 11.01"/>
      </svg>
      <h3 class="etb-success__title">${this.esc(this.s.success)}</h3>
      <p class="etb-success__text">${this.esc(this.s.successDetail)}</p>
      <div class="etb-success__summary">
        <div class="etb-success__row">
          <span class="etb-success__row-label">${this.esc(this.s.branchLabel || 'Branch')}</span>
          <span class="etb-success__row-value">${this.esc(this.getProviderName(p))}</span>
        </div>
        <div class="etb-success__row">
          <span class="etb-success__row-label">${this.esc(this.s.serviceLabel || 'Service')}</span>
          <span class="etb-success__row-value">${this.esc(svc?.name || '')}</span>
        </div>
        <div class="etb-success__row">
          <span class="etb-success__row-label">${this.esc(this.s.dateLabel || 'Date')}</span>
          <span class="etb-success__row-value">${this.formatDate(this.state.selectedDate)}</span>
        </div>
        <div class="etb-success__row">
          <span class="etb-success__row-label">${this.esc(this.s.timeLabel || 'Time')}</span>
          <span class="etb-success__row-value">${this.esc(this.state.selectedTime || '')}</span>
        </div>
      </div>
      <button type="button" class="etb-btn etb-btn--secondary" data-action="book-another">${this.esc(this.s.bookAnother)}</button>
    </div>`;
  }

  renderDesignMode() {
    this.loader.style.display = 'none';
    this.contentEl.innerHTML = `
      <div class="etb-step" style="text-align:center;padding:4rem 2rem;">
        <p style="font-size:1.6rem;font-weight:600;margin:0 0 1rem;">Eye Test Booking</p>
        <p style="font-size:1.3rem;color:rgba(var(--color-foreground),0.5);margin:0;">
          This section connects to Easy!Appointments at<br>
          <strong>${this.esc(this.config.proxyUrl || 'Not configured')}</strong><br><br>
          The live booking form will appear on the storefront.
        </p>
      </div>`;
  }

  // ── Event Binding (delegated) ──

  bindStepEvents() {
    if (this._clickHandler) this.contentEl.removeEventListener('click', this._clickHandler);
    this._clickHandler = (e) => {
      const btn = e.target.closest('[data-action]');
      if (!btn) return;

      const action = btn.dataset.action;

      switch (action) {
        case 'select-country':
          this.state.country = btn.dataset.country;
          this.state.step = 2;
          this.render();
          break;

        case 'select-branch':
          this.state.selectedProvider = this.state.providers.find(p => String(p.id) === btn.dataset.id);
          this.state.step = 3;
          this.render();
          break;

        case 'select-service':
          this.state.selectedService = this.state.services.find(s => String(s.id) === btn.dataset.id);
          this.state.selectedDate = null;
          this.state.selectedTime = null;
          this.state.availableSlots = [];
          this.state.step = 4;
          this.render();
          break;

        case 'select-time':
          this.state.selectedTime = btn.dataset.time;
          this.render();
          break;

        case 'next-to-details':
          if (this.state.selectedTime) {
            this.saveFormFields();
            this.state.step = 5;
            this.render();
          }
          break;

        case 'back':
          this.saveFormFields();
          if (this.state.step > 1) {
            this.state.step--;
            this.render();
          }
          break;

        case 'submit-booking':
          this.saveFormFields();
          this.handleBooking(btn);
          break;

        case 'book-another':
          this.state.step = 1;
          this.state.country = null;
          this.state.selectedProvider = null;
          this.state.selectedService = null;
          this.state.selectedDate = null;
          this.state.selectedTime = null;
          this.state.availableSlots = [];
          this.state.customer = { firstName: '', lastName: '', email: '', phone: '', notes: '' };
          this.state.booking = null;
          this.render();
          break;
      }
    };
    this.contentEl.addEventListener('click', this._clickHandler);

    // Date input change — check if provider is open on selected day
    const dateInput = this.contentEl.querySelector('[data-action="select-date"]');
    if (dateInput) {
      dateInput.addEventListener('change', (e) => {
        this.state.selectedDate = e.target.value;
        this.state.selectedTime = null;
        this.state.availableSlots = [];
        if (e.target.value) {
          if (this.isProviderClosedOnDate(e.target.value)) {
            // Branch is closed on this day — show message, don't fetch
            this.state.closedDay = true;
            this.render();
          } else {
            this.state.closedDay = false;
            this.loadAvailability(e.target.value);
          }
        } else {
          this.state.closedDay = false;
          this.render();
        }
      });
    }

    // Form field persistence
    this.contentEl.querySelectorAll('[data-field]').forEach(input => {
      input.addEventListener('input', () => {
        this.state.customer[input.dataset.field] = input.value;
      });
    });
  }

  saveFormFields() {
    this.contentEl.querySelectorAll('[data-field]').forEach(input => {
      this.state.customer[input.dataset.field] = input.value;
    });
  }

  // ── Availability Loading ──

  async loadAvailability(date) {
    this.state.slotsLoading = true;
    this.render();

    try {
      const slots = await this.apiFetch('availabilities', {
        providerId: this.state.selectedProvider.id,
        serviceId: this.state.selectedService.id,
        date: date,
      });

      this.state.availableSlots = Array.isArray(slots) ? slots : [];
      this.state.slotsLoading = false;
      this.render();
    } catch (e) {
      this.state.slotsLoading = false;
      this.state.availableSlots = [];
      this.render();
      this.showError(e.message || this.s.errorGeneric);
    }
  }

  // ── Booking Submission ──

  async handleBooking(btn) {
    // Prevent double submission
    if (this._bookingInProgress) return;
    this._bookingInProgress = true;

    this.hideError();

    // Validate
    const c = this.state.customer;
    const errors = [];

    this.contentEl.querySelectorAll('.etb-form__input--error').forEach(el => el.classList.remove('etb-form__input--error'));
    this.contentEl.querySelectorAll('.etb-form__error-text').forEach(el => el.remove());

    if (!c.firstName.trim()) errors.push({ field: 'firstName', msg: this.s.required });
    if (!c.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(c.email)) {
      errors.push({ field: 'email', msg: c.email.trim() ? this.s.invalidEmail : this.s.required });
    }
    if (!c.phone.trim()) errors.push({ field: 'phone', msg: this.s.required });

    if (errors.length > 0) {
      this._bookingInProgress = false;
      errors.forEach(err => {
        const input = this.contentEl.querySelector(`[data-field="${err.field}"]`);
        if (input) {
          input.classList.add('etb-form__input--error');
          const errEl = document.createElement('span');
          errEl.className = 'etb-form__error-text';
          errEl.textContent = err.msg;
          input.parentElement.appendChild(errEl);
        }
      });
      return;
    }

    // Calculate end time
    const duration = this.state.selectedService.duration || 30;
    const endTime = this.calculateEndTime(this.state.selectedTime, duration);

    btn.disabled = true;
    btn.textContent = this.s.booking;

    try {
      const result = await this.apiFetch('book', {}, 'POST', {
        customer: {
          firstName: c.firstName.trim(),
          lastName: c.lastName.trim(),
          email: c.email.trim(),
          phone: c.phone.trim(),
        },
        appointment: {
          start: `${this.state.selectedDate} ${this.state.selectedTime}:00`,
          end: `${this.state.selectedDate} ${endTime}:00`,
          serviceId: this.state.selectedService.id,
          providerId: this.state.selectedProvider.id,
          notes: c.notes.trim(),
        },
      });

      this.state.booking = result;
      this.state.step = 6;
      this._bookingInProgress = false;
      this.render();
    } catch (e) {
      this._bookingInProgress = false;
      btn.disabled = false;
      btn.textContent = this.s.confirm;
      this.showError(e.message || this.s.errorGeneric);
    }
  }

  // ── Helpers ──

  calculateEndTime(startTime, durationMinutes) {
    const [h, m] = startTime.split(':').map(Number);
    const totalMin = h * 60 + m + durationMinutes;
    const endH = String(Math.floor(totalMin / 60) % 24).padStart(2, '0');
    const endM = String(totalMin % 60).padStart(2, '0');
    return `${endH}:${endM}`;
  }

  formatDate(dateStr) {
    if (!dateStr) return '';
    try {
      const [y, m, d] = dateStr.split('-');
      return `${d}/${m}/${y}`;
    } catch {
      return dateStr;
    }
  }

  esc(str) {
    const el = document.createElement('span');
    el.textContent = str || '';
    return el.innerHTML;
  }

  showError(msg) {
    this.errorEl.textContent = msg;
    this.errorEl.style.display = 'block';
    this.errorEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  hideError() {
    this.errorEl.textContent = '';
    this.errorEl.style.display = 'none';
  }
}

customElements.define('eye-test-booking', EyeTestBooking);

// Theme editor support
if (typeof Shopify !== 'undefined') {
  document.addEventListener('shopify:section:load', function(e) {
    const el = e.target.querySelector('eye-test-booking');
    if (el) {
      el._initialized = false;
      el.connectedCallback();
    }
  });
}
